<template>
  <v-dialog
    :value="show"
    max-width="744"
    @click:outside="$emit('close')"
    @keydown.esc="$emit('close')"
  >
    <v-card
      class="pl-6 confirmation-dialog"
      :disabled="loading"
      :loading="loading"
    >
      <v-card-title style="word-break: break-word" class="ma-0 pa-0 pt-4 mb-3">
        <p class="ma-0 pa-0 blue-grey--text text--darken-4 h6">
          {{ $t('common.template_other') }}
        </p>
      </v-card-title>
      <v-card-text
        class="ma-0 pa-0 pb-2 mt-6 d-flex flex-wrap max-content-height"
      >
        <v-card
          outlined
          elevation="0"
          class="card blue-grey lighten-3 d-flex align-center flex-column justify-center mb-2 me-2"
          @click="handleGoToCreateBoard"
        >
          <v-icon color="blue-grey darken-1" size="32px" class="mb-2"
            >mdi-plus</v-icon
          >
          <p class="ma-0 subtitle2 blue-grey--text text--darken-1">
            {{ $t('common.create_from_scratch') }}
          </p>
        </v-card>
        <v-card
          v-for="(template, index) in templates"
          outlined
          elevation="0"
          class="card card-hover mb-2 me-2 pa-4"
          :key="index"
          @click="handleCreateBoard(template)"
        >
          <div class="template-image">
            <img class="image" :src="template.image" :alt="template.name" />
          </div>
          <p
            class="template-name font-size-16 font-weight-600 blue-grey--text text--darken-3 my-2"
          >
            {{ template.name }}
          </p>
          <p
            v-snip="2"
            class="mb-2 template-description caption blue-grey--text text--darken-1"
          >
            {{ template.description }}
          </p>
          <ChipList
            openOnHover
            class="template-chips"
            :items="template.chips"
            :limit="6"
          />
        </v-card>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import _ from 'lodash'
import api from '@/api'
import { mapGetters } from 'vuex'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import improvement from '@/assets/images/improvement.svg'
import routine from '@/assets/images/routine.svg'
import task from '@/assets/images/task.svg'

import ChipList from '@/components/ChipList.vue'

export default {
  name: 'TemplatesModal',

  mixins: [mixin, trackerMixin],

  components: {
    ChipList,
  },

  props: {
    show: Boolean,
  },

  data() {
    return {
      loading: false,
    }
  },

  computed: {
    ...mapGetters(['organization', 'organizationId', 'siteId']),
    hasRoutinesFeatureEnabled() {
      return Boolean(this.organization?.featuresEnabled__routines)
    },

    isTasksEnabled() {
      return Boolean(this.organization?.featuresEnabled__tasks)
    },

    templates() {
      return _.compact([
        {
          name: this.$t('boards.templates.improvement_tracker'),
          description: this.$t(
            'boards.templates.improvement_tracker_description'
          ),
          columns: [
            {
              name: this.$t('boards.templates.ongoing_ii_qa'),
              filter: {
                sites: [this.siteId],
                types: [
                  this.$constants.exploreEntityTypes.quickAction,
                  this.$constants.exploreEntityTypes.improvementIdea,
                ],
                status: [
                  this.$constants.exploreEntityStatus.open,
                  this.$constants.exploreEntityStatus.inProgress,
                ],
              },
              sort: { by: 'createdAt', order: 'desc' },
              position: 0,
            },
            {
              name: this.$t('boards.templates.completed_ii_qa'),
              filter: {
                sites: [this.siteId],
                types: [
                  this.$constants.exploreEntityTypes.quickAction,
                  this.$constants.exploreEntityTypes.improvementIdea,
                ],
                status: [this.$constants.exploreEntityStatus.completed],
                hasImplementationValidation: false,
              },
              sort: { by: 'updatedAt', order: 'desc' },
              position: 1,
            },
            {
              name: this.$t('boards.templates.pending_financial_impact'),
              filter: {
                sites: [this.siteId],
                types: [
                  this.$constants.exploreEntityTypes.quickAction,
                  this.$constants.exploreEntityTypes.improvementIdea,
                ],
                status: [this.$constants.exploreEntityStatus.completed],
                hasImplementationValidation: true,
              },
              sort: { by: 'updatedAt', order: 'desc' },
              position: 2,
            },
            {
              name: this.$t('boards.templates.validated_financial_impact'),
              filter: {
                sites: [this.siteId],
                types: [
                  this.$constants.exploreEntityTypes.quickAction,
                  this.$constants.exploreEntityTypes.improvementIdea,
                ],
                status: [this.$constants.exploreEntityStatus.completed],
                hasFinancialValidation: true,
              },
              sort: { by: 'updatedAt', order: 'desc' },
              position: 3,
            },
            {
              name: this.$t('boards.templates.ongoing_ps'),
              filter: {
                sites: [this.siteId],
                types: [this.$constants.exploreEntityTypes.problemSolving],
                status: [
                  this.$constants.exploreEntityStatus.open,
                  this.$constants.exploreEntityStatus.inProgress,
                ],
              },
              sort: { by: 'priorityScore', order: 'desc' },
              position: 4,
            },
            {
              name: this.$t('boards.templates.completed_ps'),
              filter: {
                sites: [this.siteId],
                types: [this.$constants.exploreEntityTypes.problemSolving],
                status: [this.$constants.exploreEntityStatus.completed],
                hasImplementationValidation: false,
              },
              sort: { by: 'priorityScore', order: 'desc' },
              position: 5,
            },
            {
              name: this.$t('boards.templates.ps_pending_financial_impact'),
              filter: {
                sites: [this.siteId],
                types: [this.$constants.exploreEntityTypes.problemSolving],
                status: [this.$constants.exploreEntityStatus.completed],
                hasImplementationValidation: true,
              },
              sort: { by: 'updatedAt', order: 'desc' },
              position: 6,
            },
            {
              name: this.$t('boards.templates.ps_validated_financial_impact'),
              filter: {
                sites: [this.siteId],
                types: [this.$constants.exploreEntityTypes.problemSolving],
                status: [this.$constants.exploreEntityStatus.completed],
                hasFinancialValidation: true,
              },
              sort: { by: 'updatedAt', order: 'desc' },
              position: 7,
            },
          ],
          image: improvement,
          chips: [
            this.organization.customNamings.quickAction.plural ||
              this.$t('common.quick_action_one'),
            this.organization.customNamings.problemSolving.plural ||
              this.$t('common.problem_solving_one'),
            this.organization.customNamings.improvementIdea.plural ||
              this.$t('common.improvement_idea_one'),
            this.$t('common.rev_condition.open'),
            this.$t('common.rev_condition.in_progress'),
            this.$t('common.rev_condition.completed'),
            this.$t('common.implemented'),
            this.$t('common.rev_condition.not_implemented'),
            this.$t('common.rev_condition.impact_validated'),
          ],
        },
        this.hasRoutinesFeatureEnabled && {
          name: this.$t('boards.templates.operational_oversight'),
          description: this.$t(
            'boards.templates.operational_oversight_description'
          ),
          columns: [
            {
              name: this.$t('boards.templates.started_routines'),
              filter: {
                sites: [this.siteId],
                types: [this.$constants.exploreEntityTypes.routine],
                status: [this.$constants.exploreEntityStatus.inProgress],
              },
              sort: { by: 'updatedAt', order: 'desc' },
              position: 0,
            },
            {
              name: this.$t('boards.templates.completed_routines'),
              filter: {
                sites: [this.siteId],
                types: [this.$constants.exploreEntityTypes.routine],
                status: [this.$constants.exploreEntityStatus.completed],
              },
              sort: { by: 'createdAt', order: 'desc' },
              position: 1,
            },
            {
              name: this.$t('boards.templates.top_priority_findings'),
              filter: {
                sites: [this.siteId],
                types: [this.$constants.exploreEntityTypes.finding],
                status: [
                  this.$constants.exploreEntityStatus.open,
                  this.$constants.exploreEntityStatus.inProgress,
                ],
                priority: ['high'],
              },
              sort: { by: 'priorityScore', order: 'desc' },
              position: 2,
            },
            {
              name: this.$t('boards.templates.identified_findings'),
              filter: {
                sites: [this.siteId],
                types: [this.$constants.exploreEntityTypes.finding],
                status: [
                  this.$constants.exploreEntityStatus.open,
                  this.$constants.exploreEntityStatus.inProgress,
                ],
              },
              sort: { by: 'dueDate', order: 'asc' },
              position: 3,
            },
            {
              name: this.$t('boards.templates.completed_findings'),
              filter: {
                sites: [this.siteId],
                types: [this.$constants.exploreEntityTypes.finding],
                status: [this.$constants.exploreEntityStatus.completed],
              },
              sort: { by: 'updatedAt', order: 'desc' },
              position: 4,
            },
          ],
          image: routine,
          chips: [
            this.$t('common.routine_other'),
            this.$t('common.finding_other'),
            this.$t('common.high_priority'),
            this.$t('common.rev_condition.open'),
            this.$t('common.rev_condition.in_progress'),
            this.$t('common.rev_condition.completed'),
          ],
        },
        this.isTasksEnabled && {
          name: this.$t('boards.templates.tasks_hub'),
          description: this.$t('boards.templates.tasks_hub_description'),
          columns: [
            {
              name: this.$t('boards.templates.top_priority_tasks'),
              filter: {
                sites: [this.siteId],
                types: ['task'],
                status: [
                  this.$constants.exploreEntityStatus.open,
                  this.$constants.exploreEntityStatus.inProgress,
                ],
                priority: ['high'],
              },
              sort: { by: 'dueDate', order: 'asc' },
              position: 0,
            },
            {
              name: this.$t('boards.templates.open_tasks'),
              filter: {
                sites: [this.siteId],
                types: ['task'],
                status: [this.$constants.exploreEntityStatus.open],
              },
              sort: { by: 'createdAt', order: 'desc' },
              position: 1,
            },
            {
              name: this.$t('boards.templates.ongoing_tasks'),
              filter: {
                sites: [this.siteId],
                types: ['task'],
                status: [this.$constants.exploreEntityStatus.inProgress],
              },
              sort: { by: 'dueDate', order: 'asc' },
              position: 2,
            },
            {
              name: this.$t('boards.templates.completed_tasks'),
              filter: {
                sites: [this.siteId],
                types: ['task'],
                status: [this.$constants.exploreEntityStatus.completed],
              },
              sort: { by: 'updatedAt', order: 'desc' },
              position: 3,
            },
          ],
          image: task,
          chips: [
            this.$t('tasks.task_other'),
            this.$t('common.high_priority'),
            this.$t('common.rev_condition.open'),
            this.$t('common.rev_condition.in_progress'),
            this.$t('common.rev_condition.completed'),
          ],
        },
      ])
    },
  },

  methods: {
    handleGoToCreateBoard() {
      this.trackBoardsActions('boards.Navigate', {
        section: 'create new board from scratch',
      })
      this.navigateTo({
        name: 'createBoard',
      })
    },

    async handleCreateBoard(template) {
      try {
        this.loading = true
        this.trackBoardsActions('boards.Navigate', {
          section: 'create new board from template',
          template,
        })
        const board = await this.createBoard(template)
        await this.createColumns(board.id, template.columns)
        this.showSuccess(this.$t('boards.board_created_successfully'))
        this.navigateTo({ name: 'board', params: { boardId: board.id } })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async createBoard(board) {
      const response = await api.board.create({
        organizationId: this.organizationId,
        data: {
          name: board.name,
          description: board.description,
        },
      })
      return response.data
    },

    async createColumns(boardId, columns) {
      const response = await api.board.createColumnBulk({
        organizationId: this.organizationId,
        boardId,
        columns,
      })
      return response.data
    },
  },
}
</script>

<style scoped lang="scss">
.card {
  width: 344px;
  height: 280px;
  cursor: pointer;
}

.max-content-height {
  max-height: 90vh;
  overflow: scroll;
}

.template-image {
  width: 100%;
  height: 140px;
  .image {
    border-radius: 4px;
  }
}

.template-name {
  line-height: 16px !important;
}
</style>

<style lang="scss">
.template-chips {
  .v-chip {
    font-size: 8px !important;
    height: 16px !important;
    padding: 8px !important;
  }
}
</style>
